import store from '../../../store/store';
import { asyncHandler } from '../async-handler';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import {
  REPORT_GRAPHS_API,
  REPORT_GRAPHS_ID_API,
} from '../../constants/central-api';

/** ===== Types from API shape (per your new cURLs) ===== */
export type Granularity = 'per_day' | 'per_week' | 'per_month';

export type MetricConfigs = Record<string, { color: string | 'auto' }>;

export type GraphConfigs = {
  bg_color?: string | 'auto';
  heading_color?: string | 'auto';
  font_color?: string | 'auto';
  axis_color?: string | 'auto';
  graph_width?: number | 'auto';
  graph_height?: number | 'auto';
};

export type GraphApi = {
  id: string;
  name: string;
  short_code?: string | null;
  placeholder?: string | null;
  granularity: Granularity;
  date_range: string; // e.g. '14_days', '4_weeks', 'year_to_date'
  metrics: string[]; // e.g. ['tara.insight.facebookads.adspend', ...]
  meta_configs?: unknown[];
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  shop?: { id: number; name: string; slug: string };
  tara_mcp?: {
    id: number;
    name: string | null;
    slug: string;
    logo?: string;
    is_active: boolean;
  };
  granularity_label?: string;
  date_range_label?: string;
  metrics_count?: number;

  /** new */
  graph_type?: string; // 'auto' | 'pie' | 'bar' | etc.
  graph_configs?: GraphConfigs;
  metric_configs?: MetricConfigs;
};

type GraphListResponse =
  | { success: true; data: GraphApi[] }
  | { success: false; data?: unknown };

type GraphSingleResponse =
  | { success: true; data: GraphApi }
  | { success: false; data?: unknown };

type MutateResponse =
  | { success: true; data: GraphApi }
  | { success: false; data?: unknown };

/** ===== Create / Update payloads per your new cURLs ===== */
export type GraphCreateRequest = {
  name: string;
  tara_mcp_slug: string; // e.g. "facebook_ads"
  metrics: string[]; // short-codes
  /** new */
  metric_configs?: MetricConfigs;
  graph_type?: string; // 'auto' | 'pie' | 'bar' | ...
  graph_configs?: GraphConfigs;

  granularity: Granularity; // "per_day" | "per_week" | "per_month"
  date_range: string; // "14_days" | "4_weeks" | "year_to_date"
  is_active: boolean;
};

export type GraphUpdateRequest = GraphCreateRequest;

/** ===== GET all graphs ===== */
export default async function get_graphs_api_call(): Promise<
  GraphApi[] | null
> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: REPORT_GRAPHS_API(shopSlug),
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'GET_GRAPHS';
    const customMsg = 'Graphs retrieved successfully';
    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );
    const response: GraphListResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data ?? null;
    }
    return null;
  } catch (e) {
    console.log('Error during get graphs request', e);
    return null;
  }
}

/** ===== GET single graph ===== */
export async function get_graph_api_call(
  graphId: string
): Promise<GraphApi | null> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: REPORT_GRAPHS_ID_API(shopSlug, graphId),
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'GET_GRAPH';
    const customMsg = 'Graph retrieved successfully';
    const res: any = await asyncHandler(
      requestInfo,
      actionType,
      customMsg,
      false
    );
    const response: GraphSingleResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data ?? null;
    }
    return null;
  } catch (e) {
    console.log('Error during get graph request', e);
    return null;
  }
}

/** ===== CREATE graph ===== */
export async function create_graph_api_call(
  payload: GraphCreateRequest
): Promise<GraphApi | null> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: REPORT_GRAPHS_API(shopSlug),
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'CREATE_GRAPH';
    const customMsg = 'Graph created successfully';
    const res: any = await asyncHandler(requestInfo, actionType, customMsg);
    const response: MutateResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data ?? null;
    }
    return null;
  } catch (e) {
    console.log('Error during create graph request', e);
    return null;
  }
}

/** ===== UPDATE graph ===== */
export async function update_graph_api_call(
  graphId: string,
  payload: GraphUpdateRequest
): Promise<GraphApi | null> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: REPORT_GRAPHS_ID_API(shopSlug, graphId),
      method: 'PUT',
      body: JSON.stringify(payload),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'UPDATE_GRAPH';
    const customMsg = 'Graph updated successfully';
    const res: any = await asyncHandler(requestInfo, actionType, customMsg);
    const response: MutateResponse = res?.data;

    if (res?.actionType === actionType && response?.success === true) {
      return response.data ?? null;
    }
    return null;
  } catch (e) {
    console.log('Error during update graph request', e);
    return null;
  }
}

/** ===== DELETE graph ===== */
export async function delete_graph_api_call(graphId: string): Promise<boolean> {
  try {
    const shopSlug = store.getState().user?.company?.shop_slug;
    const accessToken = store.getState().auth.authorization.accessToken;
    const sellerToken = accessToken?.sellerToken;

    const requestInfo: RequestInfo = {
      url: REPORT_GRAPHS_ID_API(shopSlug, graphId),
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
        Authorization: accessToken.type + ' ' + sellerToken,
      },
    };

    const actionType = 'DELETE_GRAPH';
    const customMsg = 'Graph deleted successfully';
    const res: any = await asyncHandler(requestInfo, actionType, customMsg);
    const response: { success: boolean } = res?.data;

    return !!(res?.actionType === actionType && response?.success === true);
  } catch (e) {
    console.log('Error during delete graph request', e);
    return false;
  }
}
